/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, type ChangeEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type PlanGoal } from '../../lib/api/itp.js';

export interface TrialCounts {
  readonly correct: string;
  readonly incorrect: string;
  readonly prompted: string;
}

export type TrialField = keyof TrialCounts;

interface TrialsEditorProps {
  readonly goals: readonly PlanGoal[];
  readonly values: Readonly<Record<string, TrialCounts>>;
  readonly onChange: (goalId: string, field: TrialField, value: string) => void;
  readonly disabled: boolean;
}

interface NumberCellProps {
  readonly goalId: string;
  readonly goalTitle: string;
  readonly field: TrialField;
  readonly label: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (goalId: string, field: TrialField, value: string) => void;
}

function NumberCell({
  goalId,
  goalTitle,
  field,
  label,
  value,
  disabled,
  onChange,
}: NumberCellProps): JSX.Element {
  const handle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(goalId, field, event.target.value);
    },
    [onChange, goalId, field],
  );
  return (
    <input
      type="number"
      min={0}
      inputMode="numeric"
      aria-label={`${goalTitle} — ${label}`}
      className="focus-ring w-16 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm disabled:opacity-60"
      value={value}
      disabled={disabled}
      onChange={handle}
    />
  );
}

const EMPTY: TrialCounts = { correct: '0', incorrect: '0', prompted: '0' };
const FIELDS: readonly TrialField[] = ['correct', 'incorrect', 'prompted'];

/** Per-goal trial-data entry for the active plan's goals (BRD M03). */
export function TrialsEditor({
  goals,
  values,
  onChange,
  disabled,
}: TrialsEditorProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {goals.map((goal) => {
        const counts = values[goal.id] ?? EMPTY;
        return (
          <div
            key={goal.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-3"
          >
            <span className="text-sm font-medium text-slate-700">{goal.title}</span>
            <div className="flex items-center gap-3">
              {FIELDS.map((field) => (
                <label key={field} className="flex items-center gap-1.5 text-xs text-slate-500">
                  {t(`therapist.note.${field}`)}
                  <NumberCell
                    goalId={goal.id}
                    goalTitle={goal.title}
                    field={field}
                    label={t(`therapist.note.${field}`)}
                    value={counts[field]}
                    disabled={disabled}
                    onChange={onChange}
                  />
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
